import React, { useState, useCallback } from 'react';
import { Button, Card } from 'react-bootstrap';
import SubtleBadge from 'components/common/SubtleBadge';
import { toast } from 'react-toastify';

import { grnApi } from 'api';
import { formatStatus } from 'utils/valueFormatters';
import { getInventoryBadgeColor } from 'constants/inventory';
import { getGrnTypeBadgeColor } from 'constants/grn/type';
import { GRN_TYPE } from 'constants/grn/grnType';
import IconButton from '../../../common/IconButton';

function isHideLine(item) {
  const { quantity, inventory_type } = item;

  if (quantity === 0) {
    return true;
  }
  return inventory_type === 'reject';
}

const GRNLineTable = ({
  grnData,
  onEdit,
  isCompleted,
  grnType,
  hasAllShelfCodes,
}) => {
  const { grnId, items } = grnData;

  const [selectedItems, setSelectedItems] = useState([]);

  const handleSelectItem = itemId => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleEdit = () => {
    const selectedRows = items.filter(item => selectedItems.includes(item.id));
    onEdit(grnId, selectedRows);
  };

  const handleExport = useCallback(async () => {
    try {
      await grnApi.getGrnLineExport(grnId); // API 호출
      toast.success('Export started successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export GRN lines. Please try again later.');
    }
  }, [grnId]);

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            GRN #{grnId}{' '}
            <SubtleBadge pill bg={getGrnTypeBadgeColor(grnType) || 'secondary'}>
              {grnType === GRN_TYPE.PURCHASE_ORDER ? 'PO' : 'Gate Pass'}
            </SubtleBadge>
            {hasAllShelfCodes && (
              <SubtleBadge pill bg="success" className="ms-2">
                Completed
              </SubtleBadge>
            )}
          </h5>

          <div className="d-flex align-items-center gap-2">
            <IconButton
              variant="falcon-default"
              size="sm"
              onClick={handleExport}
              icon="arrow-down"
              transform="shrink-3"
            >
              <span className="d-none d-sm-inline-block">Download List</span>
            </IconButton>

            {!isCompleted && (
              <Button
                variant="falcon-default"
                size="sm"
                disabled={selectedItems.length === 0}
                onClick={handleEdit}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
        <small className="text-muted d-block mt-1">
          Please enter the shelf code for each item.
        </small>
      </Card.Header>

      <Card.Body className="p-0">
        <div className="table-responsive scrollbar">
          <table className="table table-sm table-striped fs-10 mb-0">
            <thead className="bg-200 text-nowrap align-middle text-center">
              <tr>
                {!isCompleted && (
                  <th>
                    <div className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={
                          selectedItems.length === items.length &&
                          items.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                )}
                <th className="text-start">
                  {grnType === GRN_TYPE.PURCHASE_ORDER ? 'Product SKU' : 'SKU'}
                </th>
                <th className="text-start">Product Name</th>
                <th className="text-start">Vendor SKU</th>
                <th className="text-start">Vendor Name</th>
                <th className="text-start">Shelf Code</th>
                <th className="text-start">Shelf Name</th>
                <th className="text-center">Category</th>
                <th className="text-end">Quantity</th>
                <th className="text-end">Total Amount</th>
                <th className="text-start">Approved By</th>
              </tr>
            </thead>
            <tbody className="align-middle white-space-nowrap text-center">
              {items.map(item =>
                !isHideLine(item) ? (
                  <tr key={item.id}>
                    {!isCompleted && (
                      <td>
                        <div className="form-check mb-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                          />
                        </div>
                      </td>
                    )}
                    <td className="text-start">
                      {grnType === GRN_TYPE.PURCHASE_ORDER
                        ? item.product_sku
                        : item.sku}
                    </td>
                    <td className="text-start">{item.product_name}</td>
                    <td className="text-start">{item.vendor_sku}</td>
                    <td className="text-start">{item.vendor_name}</td>
                    <td className="text-start">{item.shelf_code || '-'}</td>
                    <td className="text-start">{item.shelf_name || '-'}</td>
                    <td className="text-center">
                      <SubtleBadge
                        pill
                        bg={getInventoryBadgeColor(item?.inventory_type)}
                      >
                        {formatStatus(item?.inventory_type)}
                      </SubtleBadge>
                    </td>
                    <td className="text-end">{item.quantity}</td>
                    <td className="text-end">{item.total_amount}</td>
                    <td className="text-start">{item.approved_by}</td>
                  </tr>
                ) : null,
              )}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default GRNLineTable;
